import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getReferralContents, deleteReferralContent } from '../../actions/referralContent';
import { Plus, Search, Edit, Trash2, FileText, Tag, AlertTriangle, Layers, Eye, ImageIcon } from 'lucide-react';

const ReferralContents = ({ getReferralContents, deleteReferralContent, referralContents, loading }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [contentToDelete, setContentToDelete] = useState(null);
    const [filteredContents, setFilteredContents] = useState([]);
    const [typeFilter, setTypeFilter] = useState('all');

    useEffect(() => {
        document.title = 'Referral Contents';
        getReferralContents();
    }, []);

    useEffect(() => {
        if (referralContents) {
            let filtered = referralContents.filter(content =>
                content.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                content.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                content.tagline?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                content.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                content.contentType?.toLowerCase().includes(searchTerm.toLowerCase())
            );

            if (typeFilter !== 'all') {
                filtered = filtered.filter(content =>
                    content.contentType?.toLowerCase() === typeFilter.toLowerCase()
                );
            }

            setFilteredContents(filtered);
        }
    }, [referralContents, searchTerm, typeFilter]);

    const handleDeleteClick = (content) => {
        setContentToDelete(content);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (contentToDelete) {
            deleteReferralContent(contentToDelete._id);
            setShowDeleteModal(false);
            setContentToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setContentToDelete(null);
    };

    const getTypeColor = (type) => {
        switch (type?.toLowerCase()) {
            case 'article': return 'info';
            case 'guide': return 'success';
            case 'tutorial': return 'warning';
            case 'faq': return 'secondary';
            case 'news': return 'primary';
            default: return 'dark';
        }
    };

    const getUniqueTypes = () => {
        if (!referralContents) return [];
        const types = referralContents.map(c => c.contentType).filter(Boolean);
        return [...new Set(types)];
    };

    const truncateText = (text, maxLength = 100) => {
        if (!text) return '';
        return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold">
                                    <FileText size={32} className="me-3 text-primary" />
                                    Referral Content Management
                                </h1>
                                <p className="text-muted mb-0">
                                    Manage your referral content library and publications
                                </p>
                            </div>
                            <Link
                                to="/inventory/v1.2/referral-contents/new"
                                className="btn btn-primary btn-lg shadow-sm d-flex align-items-center"
                            >
                                <Plus size={20} className="me-2" />
                                Add New Referral Content
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="row mb-4">
                    <div className="col-xl-3 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{referralContents?.length || 0}</div>
                                        <div className="small">Total Referral Contents</div>
                                    </div>
                                    <Layers size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{getUniqueTypes().length}</div>
                                        <div className="small">Content Types</div>
                                    </div>
                                    <Tag size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-info text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">
                                            {referralContents?.filter(c => c.assets && c.assets.length > 0).length || 0}
                                        </div>
                                        <div className="small">With Assets</div>
                                    </div>
                                    <ImageIcon size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-warning text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{filteredContents?.length || 0}</div>
                                        <div className="small">Filtered Results</div>
                                    </div>
                                    <Search size={32} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filter Section */}
                <div className="row mb-4">
                    <div className="col-lg-8">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <Search size={18} className="text-muted" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search referral content by name, title, tagline, description, or type..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <select
                                    className="form-select"
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                >
                                    <option value="all">All Types</option>
                                    {getUniqueTypes().map(type => (
                                        <option key={type} value={type}>
                                            {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Referral Contents Grid */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading referral contents...</p>
                    </div>
                ) : filteredContents && filteredContents.length > 0 ? (
                    <div className="row">
                        {filteredContents.map((content) => (
                            <div key={content._id} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                                <div
                                    className="card shadow-sm border-0 h-100"
                                    style={{
                                        borderRadius: '0.75rem',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 0.5rem 1rem rgba(0, 0, 0, 0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '';
                                    }}
                                >
                                    <div className="card-header bg-white border-bottom-0 pb-0">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <h5 className="card-title mb-1 text-primary fw-bold">
                                                    {content.name || content.title || 'Unnamed Referral Content'}
                                                </h5>
                                                {content.contentType && (
                                                    <span
                                                        className={`badge bg-${getTypeColor(content.contentType)} mb-2`}
                                                        style={{ fontSize: '0.75rem' }}
                                                    >
                                                        {content.contentType}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="btn-group">
                                                <Link
                                                    to={`/inventory/v1.2/referral-contents/${content._id}`}
                                                    className="btn btn-sm btn-outline-primary"
                                                    title="Edit Referral Content"
                                                >
                                                    <Edit size={14} />
                                                </Link>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDeleteClick(content)}
                                                    title="Delete Referral Content"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card-body pt-2">
                                        {/* Content Assets */}
                                        {content.assets && content.assets.length > 0 && (
                                            <div className="text-center mb-3">
                                                <div
                                                    className="d-flex justify-content-center align-items-center bg-light rounded"
                                                    style={{ height: '150px', overflow: 'hidden' }}
                                                >
                                                    {content.assets[0].assetType === 'image' ? (
                                                        <img
                                                            src={content.assets[0].asset}
                                                            alt={content.title || 'Referral Content'}
                                                            className="img-fluid rounded"
                                                            style={{
                                                                maxHeight: '140px',
                                                                maxWidth: '100%',
                                                                objectFit: 'contain'
                                                            }}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="text-muted">
                                                            <FileText size={48} />
                                                            <div className="small mt-2">{content.assets[0].assetType}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Content Details */}
                                        <div className="mb-3">
                                            {content.title && content.name !== content.title && (
                                                <h6 className="text-dark mb-2">{content.title}</h6>
                                            )}

                                            {content.tagline && (
                                                <p className="text-muted small mb-2 fst-italic">
                                                    "{content.tagline}"
                                                </p>
                                            )}

                                            {content.description && (
                                                <p className="text-muted small mb-2">
                                                    {truncateText(content.description, 80)}
                                                </p>
                                            )}
                                        </div>

                                        {/* Additional Info */}
                                        <div className="row g-2 mb-3">
                                            <div className="col-6">
                                                <div className="bg-light rounded p-2 text-center">
                                                    <div className="small text-muted">Assets</div>
                                                    <div className="fw-bold text-dark small">
                                                        {content.assets?.length || 0}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="bg-light rounded p-2 text-center">
                                                    <div className="small text-muted">FAQs</div>
                                                    <div className="fw-bold text-dark small">
                                                        {content.faqs?.length || 0}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Info Sections */}
                                        {content.info && content.info.length > 0 && (
                                            <div className="alert alert-info p-2 mb-2">
                                                <div className="small">
                                                    <strong>Info Sections:</strong> {content.info.length}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="card-footer bg-light border-top-0">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <Link
                                                to={`/inventory/v1.2/referral-contents/${content._id}`}
                                                className="btn btn-primary btn-sm"
                                            >
                                                <Edit size={14} className="me-1" />
                                                Manage
                                            </Link>
                                            <small className="text-muted">
                                                ID: {content._id?.slice(-6)}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <div className="mb-4">
                            <FileText size={64} className="text-muted" />
                        </div>
                        <h4 className="text-muted mb-3">
                            {searchTerm || typeFilter !== 'all' ? 'No referral content found' : 'No referral content created yet'}
                        </h4>
                        <p className="text-muted mb-4">
                            {searchTerm || typeFilter !== 'all'
                                ? 'No referral content matches your search criteria. Try adjusting your filters.'
                                : 'Get started by adding your first piece of referral content.'
                            }
                        </p>
                        {(!searchTerm && typeFilter === 'all') ? (
                            <Link
                                to="/inventory/v1.2/referral-contents/new"
                                className="btn btn-primary btn-lg"
                            >
                                <Plus size={20} className="me-2" />
                                Add Your First Referral Content
                            </Link>
                        ) : (
                            <button
                                className="btn btn-outline-primary"
                                onClick={() => {
                                    setSearchTerm('');
                                    setTypeFilter('all');
                                }}
                            >
                                Clear Filters
                            </button>
                        )}
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header border-0">
                                    <h5 className="modal-title d-flex align-items-center text-danger">
                                        <AlertTriangle size={24} className="me-2" />
                                        Confirm Delete
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={cancelDelete}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="text-center">
                                        <div className="mb-3">
                                            <Trash2 size={48} className="text-danger" />
                                        </div>
                                        <h6 className="mb-3">
                                            Delete Referral Content "{contentToDelete?.name || contentToDelete?.title}"?
                                        </h6>
                                        <p className="text-muted mb-4">
                                            This action cannot be undone. The referral content and all its data
                                            will be permanently deleted.
                                        </p>

                                        {contentToDelete && (
                                            <div className="alert alert-info">
                                                <small>
                                                    <strong>Referral Content Details:</strong><br />
                                                    Name: {contentToDelete.name || 'N/A'}<br />
                                                    {contentToDelete.title && (
                                                        <>Title: {contentToDelete.title}<br /></>
                                                    )}
                                                    {contentToDelete.contentType && (
                                                        <>Type: {contentToDelete.contentType}<br /></>
                                                    )}
                                                    Assets: {contentToDelete.assets?.length || 0}
                                                </small>
                                            </div>
                                        )}

                                        <div className="alert alert-warning d-flex align-items-center">
                                            <AlertTriangle size={20} className="me-2" />
                                            <small>
                                                <strong>Warning:</strong> This will permanently delete the referral content and all its data.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={cancelDelete}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={confirmDelete}
                                    >
                                        <Trash2 size={16} className="me-2" />
                                        Delete Referral Content
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Fragment>
    );
};

ReferralContents.propTypes = {
    referralContents: PropTypes.array,
    getReferralContents: PropTypes.func.isRequired,
    deleteReferralContent: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};

const mapStateToProps = state => ({
    referralContents: state.referralContent.referralContents,
    loading: state.referralContent.loading
});

export default connect(mapStateToProps, { getReferralContents, deleteReferralContent })(ReferralContents); 